import React, { useReducer } from "react"
import { useOutletContext } from "react-router-dom"
import { PlusOutlined, MinusOutlined, DeleteOutlined } from "@ant-design/icons"

import { hasAccess } from "@components/Authorization"

import useDistributionActions from "./useDistributionActions"

const LABEL_REJECT = "Reject"
const LABEL_DELETE = "Delete"
const LABEL_APPROVE = "Approve"

const reducer = (state, {
  isDeleteDisabled = true,
  isApproveDisabled = true,
  isRejectDisabled = true,
}) => {
  return {
    ...state,
    isDeleteDisabled,
    isApproveDisabled,
    isRejectDisabled
  }
}

const useSelectableActions = (table, bankAccountId) => {
  const { canApproveBankAccountTransactions } = useOutletContext()

  const [ state, dispatch ] = useReducer(reducer, {
    isDeleteDisabled: true,
    isRejectDisabled: true,
    isApproveDisabled: true,
  });

  const {
    isDeleteDisabled,
    isRejectDisabled,
    isApproveDisabled,
  } = state

  const getSelectedDistributions = () => {
    const ids = table.current.getSelectedRowKeys()
    const distributions = table.current.getItems()

    const selectedDistributions = distributions
      .filter(({ id }) => ids.includes(id))

    return selectedDistributions
  }

  const onSelectionChange = ids => {
    const hasItems = ids.length > 0

    if (!hasItems) {
      dispatch({})
      return
    }

    const distributions = table.current.getItems()

    const selectedDistributions = distributions
      .filter(({ id }) => ids.includes(id))

    const isAllSelectedPendingApproval = selectedDistributions
      .filter(({ isExternal, isApproved, status }) =>
        !isExternal &&
        !isApproved &&
        status !== 'REJECTED'
      ).length === ids.length

    const stateItems = {
      isDeleteDisabled: false
    }

    if (isAllSelectedPendingApproval) {
      stateItems.isRejectDisabled = false
      stateItems.isApproveDisabled = canApproveBankAccountTransactions(bankAccountId) === true ? false : true
    }

    dispatch(stateItems)
  }

  const afterAction = async () => {
    await table.current.indexItems()
    table.current.setSelectedRowKeys([])
    dispatch({})
  }

  const {
    approveDistribution,
    rejectDistribution,
    deleteBackfillDistributions
  } = useDistributionActions(getSelectedDistributions, afterAction)

  const actions = []

  const canDelete = hasAccess(['transactions-delete'])

  const canApproveOrReject = hasAccess(['transactions-approve'])

  if (canApproveOrReject) {
    actions.push(
    {
      icon: <PlusOutlined />,
      title: LABEL_APPROVE,
      action: approveDistribution,
      disabled: isApproveDisabled,
    },
    {
      icon: <MinusOutlined />,
      title: LABEL_REJECT,
      action: rejectDistribution,
      disabled: isRejectDisabled,
    })
  }

  if (canDelete) {
    actions.push({
      icon: <DeleteOutlined />,
      title: LABEL_DELETE,
      action: deleteBackfillDistributions,
      disabled: isDeleteDisabled,
    })
  }

  const hasActions = actions.length > 0

  if (!hasActions) {
    return []
  }

  return [ actions, onSelectionChange ]
}

export default useSelectableActions
